<template>
  <div>
    <b-card>
      <b-row>
        <b-col
          v-if="hasPermission('privacy-inherent-risk', 'create')"
          md="3"
          class="mb-1"
        >
          <b-button
            variant="outline-primary"
            :to="{ name: 'privacy-inherent-risk-add' }"
          >
            <feather-icon
              icon="PlusIcon"
              class="mr-50"
            />
            {{ $t('Agregar ARI') }}
          </b-button>
        </b-col>

        <b-col
          v-if="hasPermission('privacy-inherent-risk', 'export')"
          md="2"
          class="mb-1"
        >
          <b-button
            variant="outline-success"
            :disabled="!analyses.length"
            @click="handleExportExcel"
          >
            <feather-icon
              icon="DownloadIcon"
              class="mr-50"
            />
            {{ $t('Export Excel') }}
          </b-button>
        </b-col>

        <b-col md="2" class="mb-1">
          <b-button
            variant="outline-secondary"
            :disabled="clearFiltersDisabled"
            @click="clearFilters"
          >
            <feather-icon
              icon="TrashIcon"
              class="mr-50"
            />
            {{ $t('clear_filters') }}
          </b-button>
        </b-col>

        <b-col md="5">
          <b-form-group>
            <b-form-input
              v-model="searchQuery"
              :placeholder="$t('message.search')"
              type="search"
              class="d-inline-block"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <filters
            :levelFilterShow="true"
            :eipdFilterShow="true"
            @changeFilter="updateFilter"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-table
            ref="refARITable"
            :items="analyses"
            hover
            responsive
            :fields="tableColumns"
            primary-key="_id"
            :sort-by.sync="sortBy"
            show-empty=""
            :empty-text="isLoading ? $t('message.loading') : $t('message.no_records')"
            :sort-desc.sync="isSortDirDesc"
          >
            <!-- ID Tratamiento -->
            <template #cell(treatmentId)="data">
              <span class="font-weight-bold">{{ data.item.treatmentId }}</span>
            </template>

            <!-- Nombre Tratamiento -->
            <template #cell(treatmentName)="data">
              <span>{{ data.item.treatmentName }}</span>
            </template>

            <!-- P -->
            <template #cell(calculatedP)="data">
              <b-badge variant="light-primary" class="font-weight-bold">
                {{ data.item.calculatedP || 'N/A' }}
              </b-badge>
            </template>

            <!-- I -->
            <template #cell(calculatedI)="data">
              <b-badge variant="light-info" class="font-weight-bold">
                {{ data.item.calculatedI || 'N/A' }}
              </b-badge>
            </template>

            <!-- Riesgo Inherente -->
            <template #cell(inherentRisk)="data">
              <b-badge variant="light-dark" class="font-weight-bold">
                {{ data.item.inherentRisk || 'N/A' }}
              </b-badge>
            </template>

            <!-- Nivel (semaforizado) -->
            <template #cell(inherentLevel)="data">
              <risk-level-badge :level="data.item.inherentLevel || ''" />
            </template>

            <!-- EIPD Requerida -->
            <template #cell(eipdFinal)="data">
              <b-badge
                :variant="data.item.eipdFinal ? 'danger' : 'success'"
                class="font-weight-bold"
              >
                {{ data.item.eipdFinal ? 'Sí' : 'No' }}
              </b-badge>
            </template>

            <!-- Acciones -->
            <template #cell(actions)="data">
              <div class="flex flex-column">
                <EditButton
                  v-if="hasPermission('privacy-inherent-risk', 'edit')"
                  @clicked="router.push({name: 'privacy-inherent-risk-edit', params: { id: data.item._id }})"
                />
                <DeleteButton
                  v-if="hasPermission('privacy-inherent-risk', 'delete')"
                  v-b-modal="`delete modal ${data.item._id}`"
                />
                <b-modal
                  :id="`delete modal ${data.item._id}`"
                  centered
                  no-close-on-backdrop
                  modal-class="modal-danger"
                  ok-variant="danger"
                  cancel-variant="outline-secondary"
                  :title="$t('message.confirm_action')"
                  :ok-title="$t('message.delete')"
                  :cancel-title="$t('message.cancel')"
                  @ok="deleteAnalysis(data.item)"
                >
                  <span>{{ $tc('message.confirm_delete_ari', 0, { treatment: data.item.treatmentName }) }}</span>
                </b-modal>
              </div>
            </template>
          </b-table>
        </b-col>

        <b-col
          cols="12"
          class="d-flex justify-content-between flex-wrap"
        >
          <div class="mb-1">
            <b-form-group class="mb-0">
              <label class="d-inline-block text-sm-left mr-50">{{ $t('message.per_page') }}</label>
              <b-form-select
                id="perPageSelect"
                v-model="perPage"
                size="sm"
                :options="[10, 25, 50, 100]"
                class="per-page-selector"
              />
            </b-form-group>
          </div>

          <div class="mb-1">
            <span class="text-muted">{{ $tc('message.paginationText', 0, { from: `${((currentPage * perPage) - perPage) + (analyses.length ? 1 : 0)}`, to: `${analyses.length + ((currentPage * perPage) - perPage)}`, total: `${totalAnalyses}` }) }}</span>
          </div>

          <b-pagination
            v-model="currentPage"
            :total-rows="totalAnalyses"
            :per-page="perPage"
            first-number=""
            last-number=""
            prev-class="prev-item"
            next-class="next-item"
          />
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import { BBadge, BPagination, BTable, BModal, VBModal } from 'bootstrap-vue'
import { onMounted } from '@vue/composition-api'
import { useRouter } from '@core/utils/utils'
import { hasPermission } from '@/composables/usePermission'
import EditButton from '@/views/components/Shared/Buttons/EditButton.vue'
import DeleteButton from '@/views/components/Shared/Buttons/DeleteButton.vue'
import Filters from '@/views/organization/Filters.vue'
import RiskLevelBadge from '../../components/RiskLevelBadge.vue'
import useInherentRiskList from './useInherentRiskList'
import useExportARI from './useExportARI'
import Ripple from 'vue-ripple-directive'

export default {
  name: 'InherentRiskList',
  components: {
    BTable,
    BBadge,
    BPagination,
    EditButton,
    DeleteButton,
    Filters,
    BModal,
    RiskLevelBadge,
  },
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  setup() {
    const { router } = useRouter()

    const {
      fetchAnalyses,
      tableColumns,
      perPage,
      currentPage,
      totalAnalyses,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refARITable,
      analyses,
      deleteAnalysis,
      isLoading,
      clearFilters,
      clearFiltersDisabled,
      updateFilter,
      fetchAllAnalysesForExport,
    } = useInherentRiskList()

    const { exportToExcel } = useExportARI()

    const handleExportExcel = async () => {
      if (!hasPermission('privacy-inherent-risk', 'export')) return
      try {
        // Fetch all analyses respecting current filters
        const allAnalyses = await fetchAllAnalysesForExport()
        exportToExcel(allAnalyses)
      } catch (error) {
        console.log(error)
      }
    }

    onMounted(() => {
      fetchAnalyses()
    })

    return {
      router,
      fetchAnalyses,
      tableColumns,
      perPage,
      currentPage,
      totalAnalyses,
      searchQuery,
      sortBy,
      isSortDirDesc,
      deleteAnalysis,
      refARITable,
      analyses,
      isLoading,
      clearFilters,
      clearFiltersDisabled,
      hasPermission,
      updateFilter,
      handleExportExcel,
    }
  },
}
</script>

<style lang="scss" scoped>
.form-group {
  label {
    font-size: 0.85rem;
    font-weight: 400;
  }
}

.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
